import { useEffect, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, PosButton } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useScaleWeight } from '@/services/devices/scale'
import { ItemPromptRequest, registerItemPrompt, roundQty } from '@/services/itemPrompt'
import { formatMoney } from '@/utils'

import { NumPad } from './NumPad'

type Field = 'price' | 'quantity'

/** Price and/or quantity for the item being added; the keypad types into the field last touched. */
export const ItemPromptDialog = () => {
  const [request, setRequest] = useState<ItemPromptRequest | null>(null)
  const [price, setPrice] = useState('')
  const [quantity, setQuantity] = useState('')
  const [field, setField] = useState<Field>('price')
  // Typed by hand: the scale stops filling the quantity.
  const [typed, setTyped] = useState(false)
  const weight = useScaleWeight(!!request?.needs.quantity, request?.product.unit ?? '')

  // A settled reading fills the quantity until the cashier types one.
  useEffect(() => {
    if (weight?.stable && weight.value > 0 && !typed) setQuantity(String(weight.value))
  }, [weight, typed])

  useEffect(
    () =>
      registerItemPrompt((next) => {
        setRequest((current) => {
          current?.resolve(null)
          return next
        })
        setPrice(next.product.price > 0 ? String(next.product.price) : '')
        setQuantity('')
        setTyped(false)
        setField(next.needs.price ? 'price' : 'quantity')
      }),
    []
  )

  const close = (details: Parameters<ItemPromptRequest['resolve']>[0]) => {
    request?.resolve(details)
    setRequest(null)
  }

  const needs = request?.needs
  const typeQuantity = (v: string) => {
    setTyped(true)
    setQuantity(v)
  }
  const p = parseFloat(price) || 0
  const q = roundQty(parseFloat(quantity) || 0)
  const valid = !!request && (!needs?.price || p > 0) && (!needs?.quantity || q > 0)
  const confirm = () => valid && close({ price: needs?.price ? p : undefined, quantity: needs?.quantity ? q : undefined })

  const fieldInput = (key: Field, label: string, value: string, set: (v: string) => void, suffix?: string) => (
    <div className="grid gap-1.5">
      <Label htmlFor={`item-${key}`}>{label}</Label>
      <div className="relative">
        <Input
          id={`item-${key}`}
          value={value}
          inputMode="decimal"
          autoFocus={field === key}
          onFocus={() => setField(key)}
          onChange={(e) => set(e.target.value.replace(/[^\d.]/g, ''))}
          className={cn('h-12 text-lg tabular-nums', field === key && 'ring-2 ring-ring')}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  )

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && close(null)}>
      <DialogContent size="sm" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), confirm())}>
        {request && (
          <>
            <DialogHeader>
              <DialogTitle>{request.product.name}</DialogTitle>
              <DialogDescription>
                {needs?.price ? 'Enter the price' : `${formatMoney(request.product.price)} / ${request.product.unit}`}
                {needs?.price && needs.quantity ? ' and the quantity' : !needs?.price && needs?.quantity ? ' · enter the quantity' : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3">
              {needs?.price && fieldInput('price', `Price per ${request.product.unit}`, price, setPrice)}
              {needs?.quantity && fieldInput('quantity', 'Quantity', quantity, typeQuantity, request.product.unit)}
              {needs?.quantity && weight && (
                <button
                  type="button"
                  onClick={() => (setTyped(false), setQuantity(String(weight.value)))}
                  className="flex items-center justify-between rounded-md bg-subdued px-3 py-2 text-sm"
                >
                  <span className="text-muted-foreground">Scale{weight.stable ? '' : ' (settling…)'}</span>
                  <span className="tabular-nums text-ink">
                    {weight.value} {request.product.unit}
                  </span>
                </button>
              )}
              <NumPad
                value={field === 'price' ? price : quantity}
                onChange={field === 'price' ? setPrice : typeQuantity}
                decimals={field === 'price' ? 2 : 3}
              />
              {needs?.quantity && p > 0 && q > 0 && (
                <p className="text-right text-sm tabular-nums text-text-2">= {formatMoney(Math.round(p * q * 100) / 100)}</p>
              )}
            </div>
            <DialogFooter>
              <PosButton variant="outline" size="touch" onClick={() => close(null)}>
                Cancel
              </PosButton>
              <PosButton size="touch" disabled={!valid} onClick={confirm}>
                Add to sale
              </PosButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
